import {useEffect, useRef, useState} from 'react';

import * as api from '../api';
import {ApiError} from '../api';
import {arNum} from '../format';
import {useStudio} from '../store';
import type {VoiceItem} from '../types';

import {Skeleton} from './Elapsed';

/**
 * Identification, which is all this POC has instead of sign-in.
 *
 * Deliberately worded as identification rather than login: the name is sent back as
 * X-Studio-User and only scopes one person's projects from another's. It is not a
 * security boundary and the copy does not pretend otherwise.
 */
function SignInModal({error}: {error?: string}) {
  const s = useStudio();
  const input = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(s.user?.display_name ?? '');

  useEffect(() => {
    input.current?.focus();
  }, []);

  const submit = () => void s.signIn(name);

  return (
    <div className="scrim">
      <div className="modal">
        <div className="panel-head">
          <span className="h-title">من أنت؟</span>
        </div>
        <div className="modal-body">
          <p className="empty-note" style={{margin: '0 0 14px'}}>
            الاسم يُستخدم لفصل مشاريعك عن غيرها في هذا العرض التوضيحي فقط — ليس تسجيل دخول.
          </p>
          <input
            ref={input}
            value={name}
            placeholder="اسمك"
            autoComplete="name"
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit();
            }}
          />
          <button type="button" className="btn md" style={{marginTop: 16}} onClick={submit}>
            ابدأ
          </button>
          {error && (
            <div className="err-note" style={{marginTop: 12}}>
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * The organisation's archive — full management: browse, upload, edit, delete.
 */
function ArchiveModal({assets, error}: {assets?: {url: string; description_ar: string; s3_key: string; mime: string}[]; error?: string}) {
  const s = useStudio();
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [editingAsset, setEditingAsset] = useState<string | null>(null);
  const [editDesc, setEditDesc] = useState('');
  const [editTags, setEditTags] = useState('');

  const handleUpload = async (file: File) => {
    const descAr = window.prompt('وصف الأصل بالعربية:');
    if (!descAr) return;
    const tags = window.prompt('وسوم (مفصولة بفواصل):') ?? '';

    setUploading(true);
    try {
      await api.uploadArchiveAsset(file, {
        description_ar: descAr,
        description_en: '',
        tags,
        duration_s: 0,
      });
      // Reload archive
      void s.openArchive();
    } catch (e) {
      window.alert(`فشل الرفع: ${(e as Error).message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (s3Key: string) => {
    const filename = s3Key.replace('archive/', '');
    if (!window.confirm(`حذف "${filename}" من الأرشيف؟ لا يمكن التراجع.`)) return;
    try {
      await api.deleteArchiveAsset(filename);
      void s.openArchive();
    } catch (e) {
      window.alert(`فشل الحذف: ${(e as Error).message}`);
    }
  };

  const startEdit = (asset: {s3_key: string; description_ar: string}) => {
    setEditingAsset(asset.s3_key);
    setEditDesc(asset.description_ar);
    setEditTags('');
  };

  const saveEdit = async () => {
    if (!editingAsset) return;
    const filename = editingAsset.replace('archive/', '');
    try {
      const body: {description_ar?: string; tags?: string[]} = {};
      if (editDesc) body.description_ar = editDesc;
      if (editTags) body.tags = editTags.split(',').map((t) => t.trim()).filter(Boolean);
      await api.updateArchiveMetadata(filename, body);
      setEditingAsset(null);
      void s.openArchive();
    } catch (e) {
      window.alert(`فشل التحديث: ${(e as Error).message}`);
    }
  };

  return (
    <div className="scrim" onClick={(e) => e.target === e.currentTarget && s.closeModal()}>
      <div className="modal wide">
        <div className="panel-head">
          <span className="h-title">أرشيف الأصول المرئية</span>
          <span className="h-note">
            {assets ? `${arNum(assets.length)} أصل متاح` : 'جاري التحميل…'}
          </span>
          <span className="grow" />
          <button
            type="button"
            className="btn sm"
            disabled={uploading}
            onClick={() => fileInput.current?.click()}
          >
            {uploading ? 'جاري الرفع…' : '+ رفع أصل جديد'}
          </button>
          <input
            ref={fileInput}
            type="file"
            accept="video/*,image/*"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) void handleUpload(file);
            }}
          />
        </div>
        <div className="modal-body">
          {error && <div className="err-note">{error}</div>}
          {!assets ? (
            <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
              <Skeleton />
              <Skeleton width="80%" />
            </div>
          ) : assets.length === 0 ? (
            <p className="empty-note">الأرشيف فارغ — ارفع ملفات بالزر أعلاه.</p>
          ) : (
            <div className="arch-grid">
              {assets.map((asset) => (
                <div className="arch" key={asset.s3_key}>
                  {asset.mime.startsWith('video/') ? (
                    <video src={asset.url} controls muted playsInline preload="metadata" />
                  ) : (
                    <img src={asset.url} alt={asset.description_ar} />
                  )}

                  {editingAsset === asset.s3_key ? (
                    <div style={{display: 'flex', flexDirection: 'column', gap: 6, padding: '8px 0'}}>
                      <input
                        value={editDesc}
                        onChange={(e) => setEditDesc(e.target.value)}
                        placeholder="الوصف بالعربية"
                        style={{font: '400 13px/1.5 var(--sans)'}}
                      />
                      <input
                        value={editTags}
                        onChange={(e) => setEditTags(e.target.value)}
                        placeholder="وسوم جديدة (بفواصل)"
                        style={{font: '400 13px/1.5 var(--sans)'}}
                      />
                      <div style={{display: 'flex', gap: 6}}>
                        <button type="button" className="btn xs" onClick={() => void saveEdit()}>
                          حفظ
                        </button>
                        <button type="button" className="btn-out xs" onClick={() => setEditingAsset(null)}>
                          إلغاء
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p>{asset.description_ar}</p>
                      <div style={{display: 'flex', gap: 6, marginTop: 6}}>
                        <button
                          type="button"
                          className="btn-txt"
                          onClick={() => startEdit(asset)}
                        >
                          تعديل
                        </button>
                        <button
                          type="button"
                          className="btn-txt"
                          style={{color: 'var(--state-fail)'}}
                          onClick={() => void handleDelete(asset.s3_key)}
                        >
                          حذف
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Clone Voice Form — supports both file upload and live microphone recording.
 */
function CloneVoiceForm({
  busy,
  onClone,
  onCancel,
}: {
  busy: boolean;
  onClone: (name: string, files: File[]) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [recording, setRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const timer = useRef<number | null>(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({audio: true});
      const recorder = new MediaRecorder(stream, {mimeType: 'audio/webm'});
      chunks.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunks.current, {type: 'audio/webm'});
        setRecordedBlob(blob);
        stream.getTracks().forEach((t) => t.stop());
      };
      mediaRecorder.current = recorder;
      recorder.start();
      setRecording(true);
      setRecordSeconds(0);
      timer.current = window.setInterval(() => setRecordSeconds((s) => s + 1), 1000);
    } catch {
      window.alert('تعذّر الوصول للمايكروفون — تأكد من منح الإذن');
    }
  };

  const stopRecording = () => {
    mediaRecorder.current?.stop();
    setRecording(false);
    if (timer.current) {
      clearInterval(timer.current);
      timer.current = null;
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;
    const allFiles = [...files];
    if (recordedBlob) {
      allFiles.push(new File([recordedBlob], 'recording.webm', {type: 'audio/webm'}));
    }
    if (allFiles.length === 0) return;
    await onClone(name.trim(), allFiles);
  };

  const hasAudio = files.length > 0 || !!recordedBlob;

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16, padding: 14, background: 'var(--band)', borderRadius: 8}}>
      <span style={{font: '600 13px/1.5 var(--sans)', color: 'var(--ink)'}}>استنساخ صوت جديد</span>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="اسم الصوت" />

      {/* Tab: Upload or Record */}
      <div style={{display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap'}}>
        <label className="btn-out xs" style={{cursor: 'pointer'}}>
          📁 رفع ملف صوت
          <input
            type="file"
            accept="audio/*"
            multiple
            hidden
            onChange={(e) => {
              setFiles(Array.from(e.target.files ?? []));
              e.target.value = '';
            }}
          />
        </label>
        <span style={{color: 'var(--muted)', font: '400 12px/1 var(--sans)'}}>أو</span>
        {!recording ? (
          <button type="button" className="btn-out xs" onClick={() => void startRecording()}>
            🎙️ سجّل من المايك
          </button>
        ) : (
          <button type="button" className="btn xs" style={{background: 'var(--state-fail)', color: '#fff'}} onClick={stopRecording}>
            ⏹ إيقاف ({recordSeconds} ث)
          </button>
        )}
      </div>

      {/* Status */}
      {files.length > 0 && (
        <span style={{font: '400 12px/1.5 var(--sans)', color: 'var(--muted)'}}>
          📎 {files.length} ملف مرفوع ({files.map((f) => f.name).join(', ')})
        </span>
      )}
      {recordedBlob && !recording && (
        <span style={{font: '400 12px/1.5 var(--sans)', color: 'var(--green, #2a7)'}}>
          ✓ تسجيل جاهز ({recordSeconds} ثانية)
        </span>
      )}

      <p className="empty-note" style={{margin: 0, fontSize: 11.5}}>
        مدة التسجيل المطلوبة: 30 ثانية إلى 3 دقائق. كلما كان أطول وأوضح كانت النتيجة أفضل.
      </p>

      <div style={{display: 'flex', gap: 6}}>
        <button type="button" className="btn xs" disabled={busy || !hasAudio || !name.trim()} onClick={() => void handleSubmit()}>
          {busy ? 'جاري الاستنساخ…' : 'استنساخ'}
        </button>
        <button type="button" className="btn-out xs" onClick={onCancel}>إلغاء</button>
      </div>
    </div>
  );
}

/**
 * Voice Library — browse, preview, clone, and manage voices.
 */
function VoiceLibraryModal({
  voices,
  sceneId,
  error,
}: {
  voices?: VoiceItem[];
  sceneId?: string;
  error?: string;
}) {
  const s = useStudio();
  const [showAddForm, setShowAddForm] = useState(false);
  const [showCloneForm, setShowCloneForm] = useState(false);
  const [addName, setAddName] = useState('');
  const [addVoiceId, setAddVoiceId] = useState('');
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'pre_made' | 'cloned'>('pre_made');
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const userId = s.user?.id ?? '';

  const refreshVoices = async () => {
    try {
      await api.listVoices(userId);
    } catch {
      // ignore
    }
    // Re-open voice library to refresh
    void s.openVoiceLibrary(sceneId);
  };

  const handleAdd = async () => {
    if (!addName.trim() || !addVoiceId.trim()) return;
    setBusy(true);
    setLocalError('');
    try {
      await api.addVoice(userId, {name: addName.trim(), elevenlabs_voice_id: addVoiceId.trim()});
      setAddName('');
      setAddVoiceId('');
      setShowAddForm(false);
      await refreshVoices();
    } catch (e) {
      setLocalError(e instanceof ApiError ? e.message : 'فشلت الإضافة');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (voiceId: string) => {
    if (!window.confirm('حذف هذا الصوت؟ لا يمكن التراجع.')) return;
    try {
      await api.deleteVoice(userId, voiceId);
      await refreshVoices();
    } catch (e) {
      setLocalError(e instanceof ApiError ? e.message : 'فشل الحذف');
    }
  };

  const handleRename = async (voiceId: string) => {
    if (!editName.trim()) return;
    try {
      await api.renameVoice(userId, voiceId, editName.trim());
      setEditingId(null);
      await refreshVoices();
    } catch (e) {
      setLocalError(e instanceof ApiError ? e.message : 'فشل التعديل');
    }
  };

  const handleSetDefault = async (voiceId: string) => {
    try {
      await api.setDefaultVoice(userId, voiceId);
      await refreshVoices();
    } catch (e) {
      setLocalError(e instanceof ApiError ? e.message : 'فشل التعيين');
    }
  };

  const [loadingPreview, setLoadingPreview] = useState<string | null>(null);

  const handlePreview = async (voiceId: string) => {
    if (playingId === voiceId) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    setLoadingPreview(voiceId);
    try {
      const {preview_url} = await api.previewVoice(userId, voiceId);
      if (audioRef.current) {
        audioRef.current.pause();
      }
      const audio = new Audio(preview_url);
      audioRef.current = audio;
      audio.onended = () => setPlayingId(null);
      audio.onerror = () => { setPlayingId(null); setLoadingPreview(null); };
      await audio.play();
      setPlayingId(voiceId);
    } catch {
      setLocalError('تعذّر تشغيل المعاينة');
    } finally {
      setLoadingPreview(null);
    }
  };

  const handleSelect = (voiceId: string) => {
    if (sceneId) {
      void s.selectVoiceForScene(sceneId, voiceId);
    } else {
      s.closeModal();
    }
  };

  return (
    <div className="scrim" onClick={(e) => e.target === e.currentTarget && s.closeModal()}>
      <div className="modal wide">
        <div className="panel-head">
          <span className="h-title">مكتبة الأصوات</span>
          <span className="h-note">
            {voices ? `${arNum(voices.length)} صوت` : 'جاري التحميل…'}
          </span>
          <span className="grow" />
          <button type="button" className="btn sm" disabled={busy} onClick={() => { setShowCloneForm(true); setShowAddForm(false); }}>
            🎤 استنساخ صوت
          </button>
        </div>
        <div className="modal-body">
          {(error || localError) && <div className="err-note">{error || localError}</div>}

          {showAddForm && (
            <div style={{display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16, padding: 12, background: 'var(--band)', borderRadius: 8}}>
              <span style={{font: '600 13px/1.5 var(--sans)', color: 'var(--ink)'}}>إضافة صوت جاهز</span>
              <input value={addName} onChange={(e) => setAddName(e.target.value)} placeholder="اسم الصوت" />
              <input value={addVoiceId} onChange={(e) => setAddVoiceId(e.target.value)} placeholder="ElevenLabs Voice ID" dir="ltr" />
              <div style={{display: 'flex', gap: 6}}>
                <button type="button" className="btn xs" disabled={busy} onClick={() => void handleAdd()}>إضافة</button>
                <button type="button" className="btn-out xs" onClick={() => setShowAddForm(false)}>إلغاء</button>
              </div>
            </div>
          )}

          {showCloneForm && (
            <CloneVoiceForm
              busy={busy}
              onClone={async (name, files) => {
                setBusy(true);
                setLocalError('');
                try {
                  await api.cloneVoice(userId, name, 'ar', files);
                  setShowCloneForm(false);
                  await refreshVoices();
                } catch (e) {
                  setLocalError(e instanceof ApiError ? e.message : 'فشل الاستنساخ');
                } finally {
                  setBusy(false);
                }
              }}
              onCancel={() => setShowCloneForm(false)}
            />
          )}

          {!voices ? (
            <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
              <Skeleton />
              <Skeleton width="80%" />
            </div>
          ) : voices.length === 0 ? (
            <p className="empty-note">لا توجد أصوات — أضف صوتًا بالأزرار أعلاه.</p>
          ) : (
            <>
              {/* Tabs */}
              <div style={{display: 'flex', gap: 0, marginBottom: 12, borderBottom: '1px solid var(--rule)'}}>
                <button
                  type="button"
                  className={`btn-txt${activeTab === 'pre_made' ? ' on' : ''}`}
                  style={{padding: '8px 16px', borderBottom: activeTab === 'pre_made' ? '2px solid var(--ink)' : '2px solid transparent', fontWeight: activeTab === 'pre_made' ? 600 : 400}}
                  onClick={() => setActiveTab('pre_made')}
                >
                  🎙️ أصوات جاهزة ({arNum(voices.filter(v => v.voice_type === 'pre_made').length)})
                </button>
                <button
                  type="button"
                  className={`btn-txt${activeTab === 'cloned' ? ' on' : ''}`}
                  style={{padding: '8px 16px', borderBottom: activeTab === 'cloned' ? '2px solid var(--ink)' : '2px solid transparent', fontWeight: activeTab === 'cloned' ? 600 : 400}}
                  onClick={() => setActiveTab('cloned')}
                >
                  🎤 أصوات مستنسخة ({arNum(voices.filter(v => v.voice_type === 'cloned').length)})
                </button>
              </div>

              {/* Filtered voice list */}
              <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                {voices.filter(v => v.voice_type === activeTab).length === 0 ? (
                  <p className="empty-note">
                    {activeTab === 'pre_made'
                      ? 'لا توجد أصوات جاهزة — أضف من الزر أعلاه.'
                      : 'لا توجد أصوات مستنسخة — استنسخ صوتك من الزر أعلاه.'}
                  </p>
                ) : (
                  voices.filter(v => v.voice_type === activeTab).map((voice) => (
                <div
                  key={voice.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 12px',
                    background: voice.is_default ? 'var(--gold-tint)' : 'var(--surface-2)',
                    borderRadius: 8,
                    border: '1px solid var(--rule)',
                  }}
                >
                  {/* Preview button */}
                  <button
                    type="button"
                    className="btn-txt"
                    onClick={() => void handlePreview(voice.id)}
                    title="معاينة"
                    style={{fontSize: 16}}
                    disabled={loadingPreview === voice.id}
                  >
                    {loadingPreview === voice.id ? '⏳' : playingId === voice.id ? '⏸' : '▶'}
                  </button>

                  {/* Name + badge */}
                  <div style={{flex: 1, minWidth: 0}}>
                    {editingId === voice.id ? (
                      <div style={{display: 'flex', gap: 6, alignItems: 'center'}}>
                        <input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          style={{flex: 1, font: '400 13px/1.5 var(--sans)'}}
                          onKeyDown={(e) => { if (e.key === 'Enter') void handleRename(voice.id); }}
                        />
                        <button type="button" className="btn xs" onClick={() => void handleRename(voice.id)}>حفظ</button>
                        <button type="button" className="btn-out xs" onClick={() => setEditingId(null)}>×</button>
                      </div>
                    ) : (
                      <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
                        <span style={{font: '500 14px/1.5 var(--sans)', color: 'var(--ink)'}}>
                          {voice.name}
                        </span>
                        <span
                          style={{
                            font: '400 11px/1.5 var(--sans)',
                            color: voice.voice_type === 'cloned' ? 'var(--green)' : 'var(--slate)',
                            background: voice.voice_type === 'cloned' ? 'var(--green-tint)' : 'var(--band)',
                            padding: '1px 6px',
                            borderRadius: 4,
                          }}
                        >
                          {voice.voice_type === 'cloned' ? 'مستنسخ' : 'جاهز'}
                        </span>
                        {voice.is_default && (
                          <span style={{font: '400 11px/1.5 var(--sans)', color: 'var(--gold-ink)'}}>⭐ افتراضي</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div style={{display: 'flex', gap: 4, flexShrink: 0}}>
                    {sceneId && (
                      <button type="button" className="btn xs" onClick={() => handleSelect(voice.id)}>
                        اختر
                      </button>
                    )}
                    {!voice.is_default && (
                      <button type="button" className="btn-out xs" onClick={() => void handleSetDefault(voice.id)}>
                        ⭐ افتراضي
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn-txt"
                      onClick={() => { setEditingId(voice.id); setEditName(voice.name); }}
                    >
                      تعديل
                    </button>
                    <button
                      type="button"
                      className="btn-txt"
                      style={{color: 'var(--state-fail, var(--fail))'}}
                      onClick={() => void handleDelete(voice.id)}
                    >
                      حذف
                    </button>
                  </div>
                </div>
              ))
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function Modals() {
  const s = useStudio();

  // The store object is rebuilt on every state change, so closeModal is read through a ref
  // rather than made a dependency — otherwise this listener is torn down and rebuilt on
  // every keystroke anywhere in the app.
  const close = useRef(s.closeModal);
  close.current = s.closeModal;
  const escapable = !!s.modal && s.modal.kind !== 'signin';

  useEffect(() => {
    if (!escapable) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [escapable]);

  if (!s.modal) return null;
  if (s.modal.kind === 'signin') return <SignInModal error={s.modal.error} />;
  if (s.modal.kind === 'voices') return <VoiceLibraryModal voices={s.modal.voices} sceneId={s.modal.sceneId} error={s.modal.error} />;
  return <ArchiveModal assets={s.modal.assets} error={s.modal.error} />;
}
